import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import {
  ChevronLeft,
  Loader2,
  Save,
  CheckCircle,
  AlertCircle,
  User,
  GraduationCap,
  Clock
} from 'lucide-react'

export default function UserEdit() {
  const { userId } = useParams<{ userId: string }>()
  const navigate = useNavigate()

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState('')

  // Profile fields
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [originalEmail, setOriginalEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [role, setRole] = useState('')

  // Student fields
  const [studentId, setStudentId] = useState<string | null>(null)
  const [birthDate, setBirthDate] = useState('')
  const [ageGroup, setAgeGroup] = useState('adult')
  const [subscriptionStatus, setSubscriptionStatus] = useState('trial')
  const [programStartDate, setProgramStartDate] = useState('')
  const [lastExamDate, setLastExamDate] = useState<Date | null>(null)

  // Teacher fields
  const [teacherId, setTeacherId] = useState<string | null>(null)
  const [bio, setBio] = useState('')
  const [specializations, setSpecializations] = useState('')
  const [yearsExperience, setYearsExperience] = useState('')
  const [certifications, setCertifications] = useState('')
  const [isSuperProf, setIsSuperProf] = useState(false)

  useEffect(() => {
    if (!userId) return
    loadUser()
  }, [userId])

  const loadUser = async () => {
    setLoading(true)
    try {
      // Load profile
      const { data: profile, error: profileError } = await (supabase
        .from('profiles') as any)
        .select('*')
        .eq('id', userId)
        .single()

      if (profileError) throw profileError

      setFirstName(profile.first_name || '')
      setLastName(profile.last_name || '')
      setEmail(profile.email || '')
      setOriginalEmail(profile.email || '')
      setPhone(profile.phone || '')
      setRole(profile.role || '')
      setIsSuperProf(profile.is_super_prof || false)

      // Load student data if applicable
      if (profile.role === 'student') {
        const { data: student } = await (supabase
          .from('students') as any)
          .select('*')
          .eq('profile_id', userId)
          .single()

        if (student) {
          setStudentId(student.id)
          setBirthDate(student.birth_date || '')
          setAgeGroup(student.age_group || 'adult')
          setSubscriptionStatus(student.subscription_status || 'trial')
          setProgramStartDate(student.program_start_date || '')
        }

        // Obtener fecha del último examen rendido
        try {
          const [{ data: lastUnitExam }, { data: lastFinalExam }] = await Promise.all([
            (supabase
              .from('exam_attempts') as any)
              .select('completed_at')
              .eq('student_id', userId)
              .not('completed_at', 'is', null)
              .order('completed_at', { ascending: false })
              .limit(1),
            (supabase
              .from('final_exam_attempts') as any)
              .select('completed_at')
              .eq('student_id', userId)
              .not('completed_at', 'is', null)
              .order('completed_at', { ascending: false })
              .limit(1)
          ]) as any[]
          const candidateDates: Date[] = []
          if (lastUnitExam?.[0]?.completed_at) candidateDates.push(new Date(lastUnitExam[0].completed_at))
          if (lastFinalExam?.[0]?.completed_at) candidateDates.push(new Date(lastFinalExam[0].completed_at))
          if (candidateDates.length > 0) {
            setLastExamDate(new Date(Math.max(...candidateDates.map(d => d.getTime()))))
          }
        } catch {
          // no mostrar error si no se puede leer
        }
      }

      // Load teacher data if applicable
      if (profile.role === 'teacher') {
        const { data: teacher } = await (supabase
          .from('teachers') as any)
          .select('*')
          .eq('profile_id', userId)
          .single()

        if (teacher) {
          setTeacherId(teacher.id)
          setBio(teacher.bio || '')
          setSpecializations((teacher.specializations || []).join(', '))
          setYearsExperience(teacher.years_experience?.toString() || '')
          setCertifications((teacher.certifications || []).join(', '))
        }
      }
    } catch (err: any) {
      setError('Error al cargar el usuario: ' + err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    if (!userId) return
    if (!firstName.trim() || !lastName.trim()) {
      setError('Nombre y apellido son obligatorios')
      return
    }

    setSaving(true)
    setError('')

    try {
      // Si el email cambió, actualizar en auth.users + auth.identities + profiles via RPC
      const emailChanged = email.trim().toLowerCase() !== originalEmail.trim().toLowerCase()
      if (emailChanged) {
        const { data: emailResult, error: emailRpcError } = await (supabase.rpc as any)(
          'admin_update_user_email',
          { p_user_id: userId, p_new_email: email.trim() }
        )
        if (emailRpcError) throw emailRpcError
        if (emailResult && !emailResult.success) {
          throw new Error(emailResult.error || 'Error al actualizar el correo')
        }
      }

      // Update profile (sin email, ya lo actualizó el RPC si cambió)
      const { error: profileError } = await (supabase
        .from('profiles') as any)
        .update({
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          ...(emailChanged ? {} : { email: email.trim() }),
          phone: phone.trim() || null,
          is_super_prof: role === 'teacher' ? isSuperProf : false
        })
        .eq('id', userId)

      if (profileError) throw profileError

      // Update student fields
      if (role === 'student' && studentId) {
        const { error: studentError } = await (supabase
          .from('students') as any)
          .update({
            birth_date: birthDate || null,
            age_group: ageGroup,
            subscription_status: subscriptionStatus,
            program_start_date: programStartDate || null
          })
          .eq('id', studentId)

        if (studentError) throw studentError
      }

      // Update teacher fields
      if (role === 'teacher' && teacherId) {
        const specsArray = specializations
          .split(',')
          .map(s => s.trim())
          .filter(Boolean)
        const certsArray = certifications
          .split(',')
          .map(c => c.trim())
          .filter(Boolean)

        const { error: teacherError } = await (supabase
          .from('teachers') as any)
          .update({
            bio: bio.trim() || null,
            specializations: specsArray,
            years_experience: yearsExperience ? Number.parseInt(yearsExperience) : null,
            certifications: certsArray
          })
          .eq('id', teacherId)

        if (teacherError) throw teacherError
      }

      setSuccess(true)
      setTimeout(() => {
        navigate('/admin/users')
      }, 1500)
    } catch (err: any) {
      setError('Error al guardar: ' + err.message)
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  const isStudent = role === 'student'
  const isTeacher = role === 'teacher'

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/admin/users" className="p-2 hover:bg-muted rounded-lg">
          <ChevronLeft className="w-5 h-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold">Editar usuario</h1>
          <p className="text-muted-foreground text-sm">
            {isStudent && 'Estudiante'}
            {isTeacher && 'Profesor'}
            {!isStudent && !isTeacher && role}
          </p>
        </div>
      </div>

      {/* Success / Error banners */}
      {success && (
        <div className="flex items-center gap-2 p-4 bg-green-50 text-green-700 rounded-xl border border-green-200">
          <CheckCircle className="w-5 h-5 shrink-0" />
          Cambios guardados correctamente. Redirigiendo...
        </div>
      )}
      {error && (
        <div className="flex items-center gap-2 p-4 bg-red-50 text-red-700 rounded-xl border border-red-200">
          <AlertCircle className="w-5 h-5 shrink-0" />
          {error}
        </div>
      )}

      {/* Datos personales */}
      <div className="bg-card rounded-xl border p-6 space-y-4">
        <div className="flex items-center gap-2 mb-2">
          <User className="w-5 h-5 text-muted-foreground" />
          <h2 className="font-semibold">Datos personales</h2>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="user-first-name" className="block text-sm font-medium mb-1">Nombre <span className="text-red-500">*</span></label>
            <input
              type="text"
              id="user-first-name"
              value={firstName}
              onChange={e => setFirstName(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg bg-background focus:ring-2 focus:ring-primary focus:border-primary"
            />
          </div>
          <div>
            <label htmlFor="user-last-name" className="block text-sm font-medium mb-1">Apellido <span className="text-red-500">*</span></label>
            <input
              type="text"
              id="user-last-name"
              value={lastName}
              onChange={e => setLastName(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg bg-background focus:ring-2 focus:ring-primary focus:border-primary"
            />
          </div>
        </div>

        <div>
          <label htmlFor="user-email" className="block text-sm font-medium mb-1">Correo electrónico</label>
          <input
            type="email"
            id="user-email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            className="w-full px-3 py-2 border rounded-lg bg-background focus:ring-2 focus:ring-primary focus:border-primary"
          />
          <p className="text-xs text-muted-foreground mt-1">
            Al cambiar el correo se actualiza tanto el perfil como el inicio de sesión.
          </p>
        </div>

        <div>
          <label htmlFor="user-phone" className="block text-sm font-medium mb-1">Teléfono</label>
          <input
            type="tel"
            id="user-phone"
            value={phone}
            onChange={e => setPhone(e.target.value)}
            placeholder="+56 9 1234 5678"
            className="w-full px-3 py-2 border rounded-lg bg-background focus:ring-2 focus:ring-primary focus:border-primary"
          />
        </div>
      </div>

      {/* Datos de estudiante */}
      {isStudent && (
        <div className="bg-card rounded-xl border p-6 space-y-4">
          <div className="flex items-center gap-2 mb-2">
            <User className="w-5 h-5 text-[#0055A4]" />
            <h2 className="font-semibold">Datos del estudiante</h2>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="user-birth-date" className="block text-sm font-medium mb-1">Fecha de nacimiento</label>
              <input
                type="date"
                id="user-birth-date"
                value={birthDate}
                onChange={e => setBirthDate(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg bg-background focus:ring-2 focus:ring-primary focus:border-primary"
              />
            </div>
            <div>
              <label htmlFor="user-age-group" className="block text-sm font-medium mb-1">Grupo de edad</label>
              <select
                id="user-age-group"
                value={ageGroup}
                onChange={e => setAgeGroup(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg bg-background focus:ring-2 focus:ring-primary focus:border-primary"
              >
                <option value="adult">Adulto</option>
                <option value="children">Niño</option>
              </select>
            </div>
          </div>

          <div>
            <label htmlFor="user-program-start-date" className="block text-sm font-medium mb-1">Fecha de inicio del programa</label>
            <input
              type="date"
              id="user-program-start-date"
              value={programStartDate}
              onChange={e => setProgramStartDate(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg bg-background focus:ring-2 focus:ring-primary focus:border-primary"
            />
          </div>

          <div>
            <label htmlFor="user-subscription-status" className="block text-sm font-medium mb-1">Estado de suscripción</label>
            <select
              id="user-subscription-status"
              value={subscriptionStatus}
              onChange={e => setSubscriptionStatus(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg bg-background focus:ring-2 focus:ring-primary focus:border-primary"
            >
              <option value="trial">Trial</option>
              <option value="active">Premium (Activo)</option>
              <option value="expired">Expirado</option>
              <option value="cancelled">Cancelado</option>
            </select>
          </div>

          {/* Info de expiración de acceso */}
          {(() => {
            if (!lastExamDate) {
              return (
                <div className="flex items-start gap-2 p-3 rounded-lg bg-muted text-sm text-muted-foreground">
                  <Clock className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>Sin exámenes rendidos — el acceso no expira automáticamente.</span>
                </div>
              )
            }
            const expiresAt = new Date(lastExamDate)
            expiresAt.setMonth(expiresAt.getMonth() + 1)
            const isExpired = new Date() > expiresAt
            return (
              <div className={`flex items-start gap-2 p-3 rounded-lg text-sm ${isExpired ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-[#0055A4]/5 text-[#0055A4] border border-[#0055A4]/20'}`}>
                <Clock className="w-4 h-4 mt-0.5 shrink-0" />
                <div>
                  <p><span className="font-medium">Último examen:</span> {lastExamDate.toLocaleDateString('es-CL')}</p>
                  <p><span className="font-medium">Acceso expira:</span> {expiresAt.toLocaleDateString('es-CL')} {isExpired && <span className="font-semibold">(EXPIRADO)</span>}</p>
                </div>
              </div>
            )
          })()}
        </div>
      )}

      {/* Datos de profesor */}
      {isTeacher && (
        <div className="bg-card rounded-xl border p-6 space-y-4">
          <div className="flex items-center gap-2 mb-2">
            <GraduationCap className="w-5 h-5 text-purple-600" />
            <h2 className="font-semibold">Datos del profesor</h2>
          </div>

          <div>
            <label htmlFor="user-bio" className="block text-sm font-medium mb-1">Biografía</label>
            <textarea id="user-bio" value={bio}
              onChange={e => setBio(e.target.value)}
              rows={3}
              placeholder="Breve descripción del profesor..."
              className="w-full px-3 py-2 border rounded-lg bg-background focus:ring-2 focus:ring-primary focus:border-primary resize-none"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="user-years-experience" className="block text-sm font-medium mb-1">Años de experiencia</label>
              <input 
                id="user-years-experience"
                type="number"
                min="0"
                max="50"
                value={yearsExperience}
                onChange={e => setYearsExperience(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg bg-background focus:ring-2 focus:ring-primary focus:border-primary"
              />
            </div>
            <div>
              <label htmlFor="user-specializations" className="block text-sm font-medium mb-1">Especializaciones</label>
              <input
                id="user-specializations"
                type="text"
                value={specializations}
                onChange={e => setSpecializations(e.target.value)}
                placeholder="Francés Aplicado, Fonética..."
                className="w-full px-3 py-2 border rounded-lg bg-background focus:ring-2 focus:ring-primary focus:border-primary"
              />
              <p className="text-xs text-muted-foreground mt-1">Separadas por coma</p>
            </div>
          </div>

          <div>
            <label htmlFor="user-certifications" className="block text-sm font-medium mb-1">Certificaciones</label>
            <input
              id="user-certifications"
              type="text"
              value={certifications}
              onChange={e => setCertifications(e.target.value)}
              placeholder="DALF C2, Alliance Française..."
              className="w-full px-3 py-2 border rounded-lg bg-background focus:ring-2 focus:ring-primary focus:border-primary"
            />
            <p className="text-xs text-muted-foreground mt-1">Separadas por coma</p>
          </div>

          {/* Super Prof toggle */}
          <div className="flex items-center justify-between p-4 border rounded-lg bg-amber-50 border-amber-200">
            <div>
              <p className="font-medium text-amber-900 text-sm">Super Prof.</p>
              <p className="text-xs text-amber-700">Puede agregar notas al contenido del programa desde el modo previsualización</p>
            </div>
            <button
              type="button"
              onClick={() => setIsSuperProf(!isSuperProf)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${isSuperProf ? 'bg-amber-500' : 'bg-gray-300'}`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${isSuperProf ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-end gap-3 pb-6">
        <Link
          to="/admin/users"
          className="px-4 py-2 text-sm text-muted-foreground hover:bg-muted rounded-lg transition-colors"
        >
          Cancelar
        </Link>
        <button
          onClick={handleSave}
          disabled={saving || success}
          className="inline-flex items-center gap-2 px-5 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          Guardar cambios
        </button>
      </div>
    </div>
  )
}
